import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { EmptyState, InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { jenkinsApiRef } from '@backstage-community/plugin-jenkins';

type JenkinsProjectSummary = {
  displayName: string;
  fullName: string;
  lastBuild?: {
    number: number;
    url: string;
  };
};

type CycloneDxComponent = {
  name?: string;
  version?: string;
  type?: string;
  group?: string;
  scope?: string;
  purl?: string;
  licenses?: Array<{
    license?: {
      id?: string;
      name?: string;
    };
    expression?: string;
  }>;
};

type JenkinsSbomResponse = {
  source: 'jenkins';
  jobFullName: string;
  buildNumber: string;
  artifactPath: string;
  sbom: {
    components?: CycloneDxComponent[];
  };
};

function getComponentLicenses(component: CycloneDxComponent): string {
  if (!component.licenses?.length) {
    return '-';
  }

  return component.licenses
    .map(item => item.expression ?? item.license?.id ?? item.license?.name)
    .filter(Boolean)
    .join(', ');
}

export const JenkinsSbomCard = () => {
  const { entity } = useEntity();
  const jenkinsApi = useApi(jenkinsApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [projects, setProjects] = useState<JenkinsProjectSummary[]>([]);
  const [selectedJob, setSelectedJob] = useState<string>();
  const [sbomResponse, setSbomResponse] = useState<JenkinsSbomResponse>();
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingSbom, setIsLoadingSbom] = useState(false);
  const [error, setError] = useState<string>();

  const entityRef = {
    kind: entity.kind.toLocaleLowerCase(),
    namespace: entity.metadata.namespace ?? 'default',
    name: entity.metadata.name,
  };

  useEffect(() => {
    let isActive = true;

    const loadProjects = async () => {
      setIsLoadingProjects(true);
      setError(undefined);

      try {
        const result = (await jenkinsApi.getProjects({
          entity: entityRef,
          filter: {},
        })) as JenkinsProjectSummary[];

        if (!isActive) {
          return;
        }

        setProjects(result);
        setSelectedJob((currentSelectedJob: string | undefined) => {
          if (
            currentSelectedJob &&
            result.some(project => project.fullName === currentSelectedJob)
          ) {
            return currentSelectedJob;
          }

          return result[0]?.fullName;
        });
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Jenkins jobs.',
        );
      } finally {
        if (isActive) {
          setIsLoadingProjects(false);
        }
      }
    };

    loadProjects();

    return () => {
      isActive = false;
    };
  }, [jenkinsApi, entityRef.kind, entityRef.name, entityRef.namespace]);

  useEffect(() => {
    const selectedProject = projects.find(
      (project: JenkinsProjectSummary) => project.fullName === selectedJob,
    );

    if (!selectedProject?.lastBuild?.number) {
      setSbomResponse(undefined);
      return;
    }

    let isActive = true;

    const loadSbom = async () => {
      setIsLoadingSbom(true);
      setError(undefined);

      try {
        const searchParams = new URLSearchParams({
          jobFullName: selectedProject.fullName,
          buildNumber: String(selectedProject.lastBuild.number),
        });
        const response = await fetchApi.fetch(
          `/api/sbom/jenkins?${searchParams.toString()}`,
        );

        if (!response.ok) {
          const body = await response.json().catch(() => undefined);
          throw new Error(
            body?.error ??
              `Failed to load Jenkins SBOM: ${response.status} ${response.statusText}`,
          );
        }

        const result = (await response.json()) as JenkinsSbomResponse;

        if (!isActive) {
          return;
        }

        setSbomResponse(result);
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Jenkins SBOM.',
        );
        setSbomResponse(undefined);
      } finally {
        if (isActive) {
          setIsLoadingSbom(false);
        }
      }
    };

    loadSbom();

    return () => {
      isActive = false;
    };
  }, [fetchApi, projects, selectedJob]);

  if (isLoadingProjects) {
    return (
      <InfoCard title="SBOM">
        <Typography variant="body2">Loading Jenkins jobs...</Typography>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="SBOM">
        <Typography color="error">{error}</Typography>
      </InfoCard>
    );
  }

  if (!projects.length) {
    return (
      <EmptyState
        title="No Jenkins jobs available"
        missing="info"
        description="No Jenkins jobs were returned for this entity."
      />
    );
  }

  const selectedProject =
    projects.find(
      (project: JenkinsProjectSummary) => project.fullName === selectedJob,
    ) ?? projects[0];
  const components = sbomResponse?.sbom.components ?? [];

  return (
    <InfoCard title="Jenkins SBOM">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Select Job
          </Typography>
          <Grid container spacing={1}>
            {projects.map((project: JenkinsProjectSummary) => (
              <Grid item key={project.fullName}>
                <Button
                  variant={
                    project.fullName === selectedProject.fullName
                      ? 'contained'
                      : 'outlined'
                  }
                  color="primary"
                  onClick={() => setSelectedJob(project.fullName)}
                >
                  {project.displayName}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Showing build #{selectedProject.lastBuild?.number ?? 'N/A'} for{' '}
            {selectedProject.displayName}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {selectedProject.lastBuild?.url ? (
            <Button
              variant="outlined"
              color="primary"
              href={selectedProject.lastBuild.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Build in Jenkins
            </Button>
          ) : null}
        </Grid>

        <Grid item xs={12}>
          {isLoadingSbom ? (
            <Typography variant="body2">Loading SBOM...</Typography>
          ) : components.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Licenses</TableCell>
                  <TableCell>Package URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {components.map((component: CycloneDxComponent, index: number) => (
                  <TableRow key={`${component.name ?? 'component'}-${index}`}>
                    <TableCell>{component.name ?? '-'}</TableCell>
                    <TableCell>{component.version ?? '-'}</TableCell>
                    <TableCell>{component.type ?? '-'}</TableCell>
                    <TableCell>{component.group ?? '-'}</TableCell>
                    <TableCell>{component.scope ?? '-'}</TableCell>
                    <TableCell>{getComponentLicenses(component)}</TableCell>
                    <TableCell>{component.purl ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2">
              No SBOM components are available for the selected build.
            </Typography>
          )}
        </Grid>
      </Grid>
    </InfoCard>
  );
};
